import { withDependencies } from '@wix/thunderbolt-ioc'
import type { IAppWillMountHandler } from '@wix/thunderbolt-symbols'
import { LoggerSymbol } from '@wix/thunderbolt-symbols'
import type { ILogger } from '@wix/thunderbolt-types'
import { INIT_PRESETS_POLYFILL_SSR_MARK } from './constants'
import { supportsContainerStyleQueries } from '@wix/thunderbolt-commons'

/**
 * Reports presets polyfill performance timing to logger.
 * This runs after app initialization to retroactively report timing
 * from the inline script that ran before logger was available.
 *
 * The timing is captured via performance.mark/measure in the inline script,
 * with custom data stored in the measure's detail property.
 * This data is then reported here via meter() for FedOps metrics.
 */
export const presetsPolyfillReporter = withDependencies([LoggerSymbol], (logger: ILogger): IAppWillMountHandler => {
	return {
		appWillMount: async () => {
			try {
				// Check if the inline script created performance measures
				const entries = performance.getEntriesByName?.(INIT_PRESETS_POLYFILL_SSR_MARK)
				const measure = Array.isArray(entries) && entries.length > 0 ? (entries[0] as PerformanceMeasure) : undefined

				if (measure) {
					// Extract componentsCount from the measure's detail property
					const componentsCount = measure.detail?.componentsCount ?? 0

					// Report to FedOps as a one-off metric with component count and duration
					logger.meter(INIT_PRESETS_POLYFILL_SSR_MARK, {
						customParams: {
							componentsCount,
							duration: measure.duration,
							startTime: measure.startTime,
						},
					})
				} else if (!supportsContainerStyleQueries()) {
					// If reporter runs but measure is missing, this indicates a real problem:
					// - Feature loaded (has builder components) but inline script failed
					// - Browser doesn't support container style queries
					// - Warmup data parsing error in inline script
					logger.captureError(new Error(`Performance measure for ${INIT_PRESETS_POLYFILL_SSR_MARK} was not found`), {
						tags: { feature: 'feature-builder-component' },
						extra: { context: 'presetsPolyfillReporter' },
					})
				}
			} catch (error) {
				// Log when there's an error accessing performance API
				logger.captureError(error instanceof Error ? error : new Error(String(error)), {
					tags: { feature: 'feature-builder-component' },
					extra: { context: 'presetsPolyfillReporter-performance-api' },
				})
			}
		},
	}
})
